import { QueryCache, QueryClient } from "@tanstack/react-query";
import axiosInstance from "./axios";
import { onStaleAwareSuccess } from "./staleRefetch";
import { touchLead } from "./leadTouch";
import {
  lead_spesific_key,
  child_spesific_key,
  univ_spesific_key,
  course_spesific_key,
  user_spesific_key,
} from "@/config/endpoints";

// Optional: Define default query function that uses your axios instance
const defaultQueryFn = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}) => {
  const { data } = await axiosInstance.get(queryKey[0] as string);
  return data;
};

export const queryClient: QueryClient = new QueryClient({
  // A dashboard widget answered from its last build re-asks a few seconds
  // later (lib/staleRefetch.ts).
  queryCache: new QueryCache({
    onSuccess: (_data, query) => onStaleAwareSuccess(queryClient, query),
  }),
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      staleTime: 3 * 60 * 1000, // 3 minutes
      refetchOnWindowFocus: false,
      // Small retry budget for GETs. Hides one-off backend hiccups
      // (slow DB tick, brief 5xx, transient network blip) so the user
      // doesn't see a permanent "Failed to load" UI from a single
      // failed request. Skips auth/permission/notfound errors — those
      // are user-actionable, not transient.
      retry: (failureCount, err: any) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    },
    mutations: {
      // Mutations stay at retry=0 on purpose. Retrying a POST/PATCH
      // automatically can double-write (create two leads, send two
      // messages, etc.). The user re-clicks if it fails.
      retry: 0,
    },
  },
});

// Extra-tolerant retry preset for the leads-page hot queries
// (`useLeads`, `useLeadsListCount`, `useLeadsListStats`). These three
// are what users see on landing; under traffic spikes the server can
// briefly answer with 503 while the pg pool drains. The global 2-retry
// budget would surface a hard error after ~3s — this preset stretches
// retries to 4 with jittered backoff so the recovery window covers a
// typical spike without the user ever seeing the error UI.
export const transientRetry = {
  retry: (failureCount: number, err: unknown) => {
    const status =
      (err as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 403 || status === 404) return false;
    if (status && status >= 400 && status < 500) return false;
    return failureCount < 4;
  },
  retryDelay: (attempt: number) =>
    Math.min(800 * 2 ** attempt, 6000) + Math.floor(Math.random() * 400),
};

/**
 * Role lookups (`roles-pipeline`, `roles-pipeline/lite`, `roles-catalog`)
 * are invalidated explicitly by every role/user/branch writer, so they can
 * sit for a long time between fetches.
 */
export const ROLE_CACHE_STALE_MS = 30 * 60 * 1000;
export const ROLE_CACHE_GC_MS = 60 * 60 * 1000;

/** Invalidate and refetch every cached query (use after tenant-wide config changes). */
export function invalidateAllQueries() {
  return queryClient.invalidateQueries({ refetchType: "all" });
}

/**
 * Invalidate every LEAD-STATE cache — the views are spread across many
 * separate keys (`["leads", ...]` for grid / infinite / board, the legacy
 * `["lead-board"]`, single-lead `["get-leads", id]`, the deal caches, the
 * header count + stats tiles). Anything that mutates lead ownership, stage,
 * or branch should call this so the UI updates as soon as the request
 * returns instead of showing yesterday's snapshot until the 3-minute
 * staleTime elapses.
 *
 * Deliberately NOT here any more (2026-09-12): `roles-pipeline` /
 * `roles-catalog` (belong to `invalidateLeadAssignment` /
 * `invalidateUserCaches` / `invalidateBranchCaches` / the role mutations /
 * USER_ACCESS_REFRESH — a lead moving does not change who holds which role),
 * and `activity-log` / `lead-automation-failures` / `followups` (belong to
 * `invalidateLeadTimeline(leadId)`, targeted at the lead that changed).
 * Callers are the actor's own mutations, plus the socket bundle for a
 * re-enquiry and a lead assigned to me. A NEW lead and a source sync take the
 * lighter `counts` bundle instead — the lists, the header count + stats tiles
 * and any open custom report run (`["builder-run"]`), but not the per-lead
 * caches a lead nobody has opened yet cannot be in. Other users' changes
 * never trigger either (see useGlobalSocketEvents).
 */
export function invalidateLeadCaches() {
  // Prefix invalidations — any active observer matching one of these
  // refetches immediately.
  queryClient.invalidateQueries({ queryKey: ["leads"] });
  queryClient.invalidateQueries({ queryKey: ["get-leads"] });
  queryClient.invalidateQueries({ queryKey: ["lead-board"] });
  queryClient.invalidateQueries({ queryKey: ["get-child"] });
  // The deals-list query inside the lead detail uses queryKey `["childs"]`
  // (note the 's' suffix without the 'ren') — `useChilds` / `refetchChilds`.
  queryClient.invalidateQueries({ queryKey: ["childs"] });
  // Header total + stats matrix — moved off `useLeads` into dedicated
  // endpoints, so they have their own cache keys. Without these, the
  // four tiles (Total Leads / New Leads / Total Conversions / Total
  // Children) and the filtered total kept showing pre-bulk numbers
  // until the 3-min staleTime elapsed.
  queryClient.invalidateQueries({ queryKey: ["leads-list-count"] });
  queryClient.invalidateQueries({ queryKey: ["leads-list-stats"] });
}

/**
 * Scoped lead-cache invalidations.
 *
 * `invalidateLeadCaches()` above is a sledgehammer — it busts every
 * lead-dependent prefix at once. It was being called by nearly every
 * lead/child mutation regardless of what actually changed, so adding a
 * single note or editing one field refetched the whole board + both
 * list-stat endpoints + the role pipeline + every open activity timeline.
 *
 * The variants below let each mutation invalidate ONLY the surfaces it
 * touched. Reserve the full `invalidateLeadCaches()` for ownership / branch
 * transfer (where every scoped view genuinely moves) and the debounced
 * socket fan-out (where the cross-user change shape is unknown).
 *
 * All use React Query's default `refetchType: "active"`, so only queries
 * currently mounted refetch immediately; the rest are marked stale and
 * refetch on their next mount — no background refetch storm.
 */

/** List / board views — stage move, create, delete, ownership / branch change. */
export function invalidateLeadBoard() {
  // `["leads"]` covers grid, infinite list, status board, stage-board and
  // stage-route (all keyed under the `leads` prefix).
  queryClient.invalidateQueries({ queryKey: ["leads"] });
  queryClient.invalidateQueries({ queryKey: ["lead-board"] });
  queryClient.invalidateQueries({ queryKey: ["leads-list-count"] });
  queryClient.invalidateQueries({ queryKey: ["leads-list-stats"] });
}

/** Single-lead detail drawer + its deals (childs) section. */
export function invalidateLeadDetail(leadId?: number | string) {
  queryClient.invalidateQueries({
    queryKey: leadId != null ? ["get-leads", leadId] : ["get-leads"],
  });
  queryClient.invalidateQueries({ queryKey: ["childs"] });
  queryClient.invalidateQueries({ queryKey: ["get-child"] });
  // The card child-deal hover tooltip (`useChildHoverList`) is cached under
  // ["lead-children-hover", leadId] and renders child stage/status/name — a
  // child edit must refresh it too, else the tooltip serves stale data after
  // the cards remount. Every child writer routes through invalidateLeadDetail,
  // so busting it here covers updateChild / child updateStage / patchChild.
  queryClient.invalidateQueries({ queryKey: ["lead-children-hover"] });
  refetchEntityTracksAfterAutomation();
}

/**
 * Refresh the drawer's Tracks tab (`["entity-tracks", type, id]`) after any
 * lead/deal write — and AGAIN a moment later.
 *
 * Automations that assign tracks ("Type Of Interview = X → assign track X")
 * run AFTER the save's transaction commits, off the request: the save's own
 * response returns before the track row exists, so a single invalidation on
 * success refetches a list that doesn't have it yet, and the user reloads the
 * page to see it. Two follow-up invalidations (1.5 s, 4 s) cover the
 * in-process fan-out and the queued paths without sockets — plain React
 * Query refetches on a query that is already mounted. Idle tabs cost nothing:
 * an invalidation only refetches queries that are on screen. Collapsed into
 * one timer set per burst so rapid saves don't stack timers.
 */
let tracksRefetchTimers: number[] = [];
export function refetchEntityTracksAfterAutomation() {
  const bust = () => queryClient.invalidateQueries({ queryKey: ["entity-tracks"] });
  bust();
  for (const t of tracksRefetchTimers) window.clearTimeout(t);
  tracksRefetchTimers = [1500, 4000].map((ms) => window.setTimeout(bust, ms));
}

/**
 * Dashboard Recent Activity feed (`["v2-recent-activity", params]`, see
 * `recentActivityQueryKey`). No socket refreshes it any more, so the actor's
 * own activity-writing mutations call this. Only a mounted feed refetches;
 * otherwise it is marked stale and refetches on its next mount.
 */
export function invalidateRecentActivityFeed() {
  queryClient.invalidateQueries({ queryKey: ["v2-recent-activity"] });
}

/**
 * Activity timeline + follow-ups + the automation-failure banner, plus the
 * dashboard Recent Activity feed.
 *
 * With a `leadId` the busts are exact to that lead: the activity-log key is
 * `["activity-log", childId, leadId, category]` and the failures key is
 * `["lead-automation-failures", leadId, entityType, entityId, limit]`, so
 * the socket handlers (which know the lead that moved) refetch ONE open
 * drawer's timeline, not every open drawer's.
 * Without a `leadId` (the actor's own mutation paths) it stays the prefix.
 */
export function invalidateLeadTimeline(leadId?: number | string | null) {
  const id = leadId != null && leadId !== "" ? Number(leadId) : NaN;
  // Activity on a known lead = the card's "last updated" corner moves now.
  if (Number.isFinite(id)) touchLead(id);
  invalidateLeadTimelines(Number.isFinite(id) ? new Set([id]) : null);
}

/**
 * `invalidateLeadTimeline` for several leads in one pass. `["followups"]` and
 * the feed are not per-lead, so looping the single-lead version re-busted
 * them once per lead in the same tick, and each repeat cancelled and re-sent
 * the in-flight GET. `null` = every lead (prefix busts).
 */
export function invalidateLeadTimelines(leadIds: ReadonlySet<number> | null) {
  if (leadIds == null) {
    queryClient.invalidateQueries({ queryKey: ["activity-log"] });
    queryClient.invalidateQueries({ queryKey: ["lead-automation-failures"] });
  } else {
    if (leadIds.size === 0) return;
    queryClient.invalidateQueries({
      predicate: (q) =>
        q.queryKey[0] === "activity-log" && leadIds.has(Number(q.queryKey[2])),
    });
    queryClient.invalidateQueries({
      predicate: (q) =>
        q.queryKey[0] === "lead-automation-failures" &&
        leadIds.has(Number(q.queryKey[1])),
    });
  }
  queryClient.invalidateQueries({ queryKey: ["followups"] });
  invalidateRecentActivityFeed();
}

/**
 * Assignment changed — the assign modal's eligibility math reads off the
 * role pipeline + the lead caches, and assignee chips render on board cards
 * and the detail drawer.
 */
export function invalidateLeadAssignment() {
  queryClient.invalidateQueries({ queryKey: ["roles-pipeline"] });
  queryClient.invalidateQueries({ queryKey: ["roles-catalog"] });
  invalidateLeadBoard();
  invalidateLeadDetail();
}

/**
 * Caches that depend on the stage / track pipeline shape — the leads board
 * (stage columns, badges, levels), dashboard breakdowns, and the role
 * pipeline (stage / track permission blueprints). Use after stage / track
 * CRUD instead of refetching the entire query cache. Pipeline / stage /
 * route queries are refetched by their own hooks at the call site.
 */
export function invalidatePipelineDependentCaches() {
  invalidateLeadBoard();
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  queryClient.invalidateQueries({ queryKey: ["roles-pipeline"] });
  queryClient.invalidateQueries({ queryKey: ["roles-catalog"] });
}

/**
 * Invalidate every cached query but only refetch the ones currently on
 * screen (refetchType defaults to "active"); the rest refetch on next mount.
 * Use for a user's own access / permission change, where data scope shifts
 * everywhere but forcing a background refetch of every inactive query (what
 * `invalidateAllQueries`'s `refetchType: "all"` does) is wasteful.
 */
export function invalidateAllActiveQueries() {
  return queryClient.invalidateQueries();
}

/**
 * Invalidate every cache that depends on user identity — list, single user,
 * the role/branch pipelines that gate assignment, and the lead caches that
 * render assignee chips (name, avatar, role badge). Call this after any
 * user CRUD (create, profile edit, role change, block/unblock, delete).
 * Branch list is included because user.branches[] feeds the branch row's
 * userBranches[] aggregate shown in the branch table.
 */
export function invalidateUserCaches() {
  queryClient.invalidateQueries({ queryKey: ["users"] });
  queryClient.invalidateQueries({ queryKey: ["get-user"] });
  // Blocking/unblocking changes this set; without it the header badge and
  // every lite-pipeline picker keep the old answer for the whole cache window.
  queryClient.invalidateQueries({ queryKey: ["blocked-user-ids"] });
  queryClient.invalidateQueries({ queryKey: ["roles-pipeline"] });
  queryClient.invalidateQueries({ queryKey: ["roles-catalog"] });
  queryClient.invalidateQueries({ queryKey: ["branches"] });
  queryClient.invalidateQueries({ queryKey: ["leads"] });
  queryClient.invalidateQueries({ queryKey: ["get-leads"] });
  queryClient.invalidateQueries({ queryKey: ["status-based-leads"] });
  queryClient.invalidateQueries({ queryKey: ["stage-board-leads"] });
}

/**
 * Invalidate every cache that depends on branch state — list, single branch,
 * user list (each user carries `user.branches[]`), the role pipeline that
 * uses branch membership for assignment eligibility, and the lead caches
 * that render branch chips on rows. Call this after any branch CRUD.
 */
export function invalidateBranchCaches() {
  queryClient.invalidateQueries({ queryKey: ["branches"] });
  queryClient.invalidateQueries({ queryKey: ["archived-pipelines"] });
  queryClient.invalidateQueries({ queryKey: ["get-branch"] });
  queryClient.invalidateQueries({ queryKey: ["users"] });
  queryClient.invalidateQueries({ queryKey: ["roles-pipeline"] });
  queryClient.invalidateQueries({ queryKey: ["roles-catalog"] });
  queryClient.invalidateQueries({ queryKey: ["leads"] });
  queryClient.invalidateQueries({ queryKey: ["get-leads"] });
  // Forking a branch flips the server pipeline gate → the pipeline/stage/board
  // reads change. Bust them so the Stage Management + board UIs reflect the fork.
  queryClient.invalidateQueries({ queryKey: ["pipeline-stage"] });
  queryClient.invalidateQueries({ queryKey: ["stages"] });
  queryClient.invalidateQueries({ queryKey: ["v2-report-pipeline"] });
  // The sidebar lists each branch pipeline (and its stages) by branch, so a
  // fork, an edit or a branch change must refresh it now, not in 5 minutes.
  queryClient.invalidateQueries({ queryKey: ["router-tabs"] });
}

/**
 * Invalidate every cache that depends on custom field metadata — the list
 * (`["connections"]`), single field (`["get-custom-input", id]`), the
 * resolve-defaults query used by lead/preference forms, and the lead
 * caches that render custom field values inline. Call this after any
 * custom input CRUD (create, update, delete, text→selector conversion).
 */
export function invalidateCustomFieldCaches() {
  queryClient.invalidateQueries({ queryKey: ["connections"] });
  queryClient.invalidateQueries({ queryKey: ["get-custom-input"] });
  queryClient.invalidateQueries({ queryKey: ["resolve-custom-defaults"] });
  // The per-model CF CATALOG that `useSpesificCustoms` renders the visible
  // fields from — its query key starts with these *_spesific_key constants.
  // These were MISSING here, so a newly created / edited / deleted / reordered
  // custom field did not bust the catalog cache and could stay hidden for up
  // to the (3 min global) staleTime — even for the admin who just changed it.
  // Adding them makes CF changes appear immediately. (Prefix match: passing
  // just the key string invalidates every [key, modelName, stageId, campaignId]
  // variant.)
  queryClient.invalidateQueries({ queryKey: [lead_spesific_key] });
  queryClient.invalidateQueries({ queryKey: [child_spesific_key] });
  queryClient.invalidateQueries({ queryKey: [univ_spesific_key] });
  queryClient.invalidateQueries({ queryKey: [course_spesific_key] });
  queryClient.invalidateQueries({ queryKey: [user_spesific_key] });
  queryClient.invalidateQueries({ queryKey: ["leads"] });
  queryClient.invalidateQueries({ queryKey: ["get-leads"] });
}

/**
 * Hard reset for logout. `invalidateQueries` keeps the cached payloads and
 * just marks them stale, so the next user briefly sees the previous tenant's
 * data before the refetch lands — that's the source of the post-login UI
 * errors. We instead cancel any in-flight requests and drop every cache
 * entry so the next mount starts from empty.
 */
export async function resetQueryClientForLogout() {
  await queryClient.cancelQueries();
  queryClient.removeQueries();
  queryClient.clear();
  // Reset the leads-prefetch idempotency guard so the next session
  // re-warms the cache from scratch. Imported lazily to avoid a cycle
  // with the leads feature.
  const { resetLeadsPrefetchGuard } = await import(
    "@/features/leads/api/prefetchLeadsList"
  );
  resetLeadsPrefetchGuard();
}
